import { FC } from 'react';
import type { DreMes, DrePayload } from '@/types/dashboards';
import { fmtBRL, fmtBRLCompacto, fmtData, fmtDataHora, fmtPct } from '@/utils/painelFormat';
import Aviso from '../Aviso';
import { STATUS } from '../chartTheme';

interface Props {
  payload: DrePayload;
  mes: DreMes | undefined;
}

const AvisoCopia: FC<{ payload: DrePayload }> = ({ payload }) => {
  const { posicao } = payload;
  if (!posicao.contabil_atrasado) return null;
  return (
    <Aviso icone="update" cor={STATUS.atencao} titulo="Contábil da réplica desatualizado" testId="dre-aviso-copia">
      A última cópia do contábil é de {fmtDataHora(posicao.contabil_copiado_em)} ({posicao.dias_desde_copia_contabil} dias).
      O que a contabilidade lançou depois disso ainda não aparece aqui.
    </Aviso>
  );
};

const AvisoMesIncompleto: FC<{ mes: DreMes | undefined }> = ({ mes }) => {
  if (!mes?.contabil_incompleto || mes.cobertura_contabil === null) return null;
  return (
    <Aviso icone="hourglass_top" cor={STATUS.atencao} titulo={`${mes.label}: contábil ainda sendo lançado`} testId="dre-aviso-incompleto">
      A receita no contábil é {fmtPct(mes.cobertura_contabil * 100)} da receita das notas fiscais do mês (
      {fmtBRLCompacto(mes.receita_contabil)} de {fmtBRLCompacto(mes.receita_notas)}). Custos e despesas também podem estar
      incompletos.
    </Aviso>
  );
};

const AvisoSemContabil: FC<{ payload: DrePayload }> = ({ payload }) => {
  if (!payload.meses_sem_contabil.length) return null;
  const texto = payload.meses_sem_contabil.map((m) => `${m.label} (notas: ${fmtBRLCompacto(m.receita_notas)})`).join(', ');
  return (
    <Aviso icone="event_busy" cor={STATUS.atencao} titulo="Mês sem lançamento contábil" testId="dre-aviso-sem-contabil">
      {texto}: já tem nota fiscal, mas nenhum lançamento no contábil da réplica. O mês entra na DRE quando a contabilidade
      lançar.
    </Aviso>
  );
};

const AvisoClassificacao: FC<{ payload: DrePayload }> = ({ payload }) => {
  const contas = payload.contas_sem_classificacao;
  if (!contas.length && !payload.posicao.depara_velho) return null;
  const total = contas.reduce((s, c) => s + c.total, 0);
  return (
    <Aviso icone="rule" cor={STATUS.serio} titulo="Classificação do Datasul" testId="dre-aviso-classificacao">
      A classificação das contas (programa bi060) foi copiada em {fmtData(payload.posicao.depara_copiado_em)}.
      {contas.length > 0 &&
        ` ${contas.length} conta${contas.length > 1 ? 's' : ''} com lançamento não estão nela (${fmtBRL(total)} no período) e aparecem em "Contas sem classificação no Datasul".`}
    </Aviso>
  );
};

/** O que muda a leitura dos números: cópia velha, mês incompleto, conta sem classificação. */
const AvisosDre: FC<Props> = ({ payload, mes }) => (
  <div className="flex flex-col gap-3">
    <AvisoCopia payload={payload} />
    <AvisoMesIncompleto mes={mes} />
    <AvisoSemContabil payload={payload} />
    <AvisoClassificacao payload={payload} />
    {payload.avisos.map((a) => (
      <Aviso key={a} icone="info" cor={STATUS.atencao} titulo="Orçamento" testId="dre-aviso-orcamento">
        {a}
      </Aviso>
    ))}
  </div>
);

export default AvisosDre;
